import { ausrehabApi as baseApi } from '../baseApi';
import type { RequestProps, ReportProps, UserProps, SupplierProps, PaymentSummaryProps } from '../../models';

export interface GetRequestsResponse extends RequestProps {
	candidateName: string;
	user: UserProps;
	supplier: SupplierProps;
	updatedAt: Date;
}

const requestApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		geocoding: builder.query({
			query: (args) => {
				const { location } = args;

				return {
					url: 'locations',
					params: {
						location,
					},
				};
			},
		}),

		getRequests: builder.query<GetRequestsResponse[], void>({
			query: () => ({
				url: 'requests',
			}),
			providesTags: ['Request'],
			transformResponse: (response: GetRequestsResponse[]) => {
				return response
					.filter((res) => res.candidate)
					.map((res) => {
						return {
							...res,
							candidateName: `${res.candidate.firstname} ${res.candidate.lastname}`,
						};
					});
			},
		}),

		getRequest: builder.query({
			query: (id) => ({
				url: `requests/${id}`,
			}),
			providesTags: ['Request'],
		}),

		getRequestPaymentSummary: builder.query<PaymentSummaryProps, string>({
			query: (id) => ({
				url: `requests/${id}/paymentSummary`,
			}),
			providesTags: ['Request'],
		}),

		getRequestReports: builder.query<ReportProps[], number>({
			query: (id) => ({
				url: `requests/${id}/reports`,
			}),
			providesTags: ['Request-Reports'],
		}),

		uploadRequestReport: builder.mutation({
			query: (payload) => {
				const { requestId, reports } = payload;

				return {
					url: `requests/${requestId}/reports`,
					method: 'POST',
					body: {
						reports,
					},
				};
			},
			invalidatesTags: ['Request-Reports'],
		}),

		deleteRequestReport: builder.mutation({
			query: (payload) => {
				const { requestId, reportName } = payload;

				return {
					url: `requests/${requestId}/reports`,
					method: 'DELETE',
					params: {
						report_name: reportName,
					},
				};
			},
			invalidatesTags: ['Request-Reports'],
		}),

		getRequestReport: builder.query<ReportProps, { requestId: number; reportName: string; action?: string }>({
			query: (args) => {
				const { requestId, reportName, action } = args;
				return {
					url: `requests/${requestId}/reports/${reportName}`,
					method: 'HEAD',
					params: {
						action,
					},
				};
			},
			transformResponse: (response: ReportProps, meta: { response: Response }) => {
				return {
					...response,
					uri: meta.response.headers.get('request-report-uri') ?? '',
				};
			},
			providesTags: ['Request'],
		}),

		addRequest: builder.mutation({
			invalidatesTags: ['Request'],
			query: (payload) => {
				payload.id = undefined;

				return {
					url: 'requests',
					method: 'POST',
					body: payload,
				};
			},
		}),

		delRequest: builder.mutation({
			query: (id) => ({
				url: `requests/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Request'],
		}),

		// use updateRequestStatus for status update
		updateRequest: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}`,
					method: 'PUT',
					body: payload,
				};
			},
			invalidatesTags: ['Request'],
		}),

		updateRequestStatus: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/status`,
					method: 'PUT',
					body: {
						status: payload.status,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),

		updateRequestAssessmentDate: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/assessmentDates`,
					method: 'PUT',
					body: {
						assessmentDates: [payload.assessmentDate],
					},
				};
			},
			invalidatesTags: ['Request'],
		}),

		payRequest: builder.mutation({
			query: (payload) => {
				const { requestId, items } = payload;

				return {
					url: 'payments',
					method: 'POST',
					body: {
						requestId,
						items,
					},
				};
			},
		}),

		cancelRequest: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/cancel`,
					method: 'DELETE',
				};
			},
			invalidatesTags: ['Request'],
		}),

		setRequestAdminNote: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/adminNote`,
					method: 'PUT',
					body: {
						adminNote: payload.note,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),

		setRequestAccountNote: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/accountNote`,
					method: 'PUT',
					body: {
						accountNote: payload.note,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGeocodingQuery,
	useGetRequestQuery,
	useGetRequestsQuery,
	useGetRequestReportsQuery,
	useGetRequestPaymentSummaryQuery,

	// mutation
	useDelRequestMutation,
	useUpdateRequestMutation,
	useUpdateRequestStatusMutation,
	useUpdateRequestAssessmentDateMutation,
	useAddRequestMutation,
	usePayRequestMutation,
	useCancelRequestMutation,
	useUploadRequestReportMutation,
	useDeleteRequestReportMutation,
	useLazyGetRequestReportQuery,
	useLazyGetRequestQuery,
	useSetRequestAdminNoteMutation,
	useSetRequestAccountNoteMutation,
} = requestApi;
